import * as DateUtil from "@travellocal/utils/dateHelpers";
import globalConfig from "@travellocal/utils/env";
import { EntityConverter } from "../../helpers/entityConverter";
import { neverHere } from "@travellocal/utils/tsHelpers";
import { formatQueryString } from "@travellocal/utils/urlHelpers";
import * as ApiParams from "./apiParameters";
import { Fetcher, HttpMethod } from "./fetcher";

export class SystemApiClient {
  private baseUrl: string;
  private useUserCurrency: boolean;

  public constructor(private fetchAuthToken?: () => string) {
    this.baseUrl = globalConfig.apiBaseUrl;
  }

  private sendJson<T, R>(
    method: HttpMethod,
    url: string,
    payload?: T,
    customCurrencyCode?: string,
    isPrimary?: boolean
  ): Promise<IFetchApiResponse<R>> {
    return Fetcher.sendJson<T, R>(method, `${this.baseUrl}${url}`, payload, this.fetchAuthToken ? this.fetchAuthToken() : null, customCurrencyCode, isPrimary);
  }

  private sendForm<T, R>(method: string, url: string, payload?: T): Promise<IFetchApiResponse<R>> {
    return Fetcher.sendForm<T, R>(method, `${this.baseUrl}${url}`, payload, this.fetchAuthToken ? this.fetchAuthToken() : null);
  }

  public sendNewsletterSubscription(newsletterInput: IHubspotNewsletterSubscription) {
    return this.sendJson<IHubspotNewsletterSubscription, void>("POST", `/newsletter/subscribe`, newsletterInput);
  }

  public getLtoByIdOrSlug(slugOrId: number | string) {
    return this.sendJson<void, ILto>("GET", `/ltos/${slugOrId}`);
  }

  public getLtosByLocationSlug(locationSlug: string) {
    return this.sendJson<void, ILtoOutput>("GET", `/locations/${locationSlug}/ltos`);
  }

  public setLocationDefaultLto(locationId: number, ltoId: number) {
    return this.sendJson<{ id: number; defaultLtoId: number }, ILocation>("PUT", `/locations/${locationId}/default-lto`, { id: locationId, defaultLtoId: ltoId });
  }

  public setLtoLocationConfig(locationId: number, ltoId: number, config: Partial<ILtoLocationConfig> = {}) {
    return this.sendJson<Partial<ILtoLocationConfig>, IApiResponse<ILtoLocation>>("PUT", `/locations/${locationId}/ltos-config/${ltoId}`, config);
  }

  public removeLtoFromLocation(locationId: number, ltoId: number) {
    return this.sendJson<void, void>("DELETE", `/locations/${locationId}/ltos-config/${ltoId}`);
  }

  public getLtos(params?: Partial<ApiParams.LtosFilterSet>) {
    return this.sendJson<void, IApiResponse<ILtoSummary[]>>("GET", `/ltos${formatQueryString(params)}`);
  }

  public createLto(newLtoInput: INewLtoInput) {
    return this.sendJson<INewLtoInput, ICreationResult>("POST", `/ltos`, newLtoInput);
  }

  public updateLtoProfile(ltoProfileUpdateInput: ILtoProfileUpdateInput) {
    return this.sendJson<ILtoProfileUpdateInput, void>("PUT", `/ltos/${ltoProfileUpdateInput.id}/profile`, ltoProfileUpdateInput);
  }

  public updateUserProfile(userProfileUpdateInput: IUserUpdate) {
    return this.sendJson<IUserUpdate, IApiResponse<IUser>>("PUT", `/users/${userProfileUpdateInput.id}`, userProfileUpdateInput);
  }

  public getUserProfile(userId: number) {
    return this.sendJson<number, IApiResponse<IUser>>("GET", `/users/${userId}`);
  }

  public updateLtoSettings(ltoSettingsUpdateInput: ILtoSettingsUpdateInput) {
    return this.sendJson<ILtoSettingsUpdateInput, ILto>("PUT", `/ltos/${ltoSettingsUpdateInput.id}/settings`, ltoSettingsUpdateInput);
  }

  public enableLto(ltoId: number) {
    return this.sendJson<void, void>("PUT", `/ltos/${ltoId}/enabled`);
  }

  public disableLto(ltoId: number) {
    return this.sendJson<void, void>("DELETE", `/ltos/${ltoId}/enabled`);
  }

  public getExcursionsByLtoIdAndCountryId(ltoId: number, countryId: number, currencyCode?: string) {
    return this.sendJson<void, IPage<IExcursion>>("GET", `/excursions?ltoId=${ltoId}&countryId=${countryId}`, null, currencyCode);
  }

  public getEmployeesByLtoId(ltoId: number) {
    return this.sendJson<void, IEditableLtoEmployee[]>("GET", `/ltos/${ltoId}/employees`);
  }

  public updateLtoEmployee(input: ILtoEmployeeUpdate) {
    return this.sendJson<ILtoEmployeeUpdate, IEditableLtoEmployee>("PUT", `/users/${input.id}/lto-employee`, input);
  }

  public addLtoEmployee(input: INewLtoEmployeeInput) {
    return this.sendJson<INewLtoEmployeeInput, IEditableLtoEmployee>("POST", `/users/lto-employee`, input);
  }

  public getLtoTerms(ltoId: number) {
    return this.sendJson<void, ILtoTerms>("GET", `/ltos/${ltoId}/terms`);
  }

  public getBookingTermsVersions(ltoId: number) {
    return this.sendJson<void, IApiResponse<IBookingTermsListItem[]>>("GET", `/ltos/${ltoId}/terms/list`);
  }

  public getBookingTerms(termsId: number) {
    return this.sendJson<void, IApiResponse<IBookingTerms>>("GET", `/booking-terms/${termsId}`);
  }

  public createBookingTerms(body: INewBookingTerms) {
    return this.sendJson<INewBookingTerms, IApiResponse<IBookingTerms>>("POST", `/booking-terms/`, body);
  }

  public updateBookingTerms(termsId: number, body: IBookingTermsUpdate) {
    return this.sendJson<IBookingTermsUpdate, IApiResponse<IBookingTerms>>("PUT", `/booking-terms/${termsId}`, body);
  }

  public initialEnquiry(model: INewInitialEnquiryMessageInput | INewAnonymousInitialEnquiryMessageInput) {
    return this.sendJson<typeof model, IEnquiryCreationResult>("POST", "/messages/initial-enquiry", model);
  }

  public getEnquiry(enquiryId: number) {
    return this.sendJson<void, IApiResponse<IEnquiryOutput>>("GET", `/enquiries/${enquiryId}`);
  }

  public updateEnquiry(enquiryId: number, update: IEnquiryUpdate) {
    return this.sendJson<IEnquiryUpdate, IApiResponse<IEnquiryOutput>>("PUT", `/enquiries/${enquiryId}`, update);
  }

  public switchEnquiryLto(enquiryId: number, ltoId: number) {
    const body = { ltoId };
    return this.sendJson<IEnquiryUpdate, IApiResponse<IEnquiryOutput>>("PUT", `/enquiries/${enquiryId}/switch-lto`, body);
  }

  public assignEnquiryLto(enquiryId: number) {
    return this.sendJson<IEnquiryUpdate, IApiResponse<IEnquiryOutput>>("PUT", `/enquiries/${enquiryId}/assign-lto`);
  }

  public sendEnquiryItinerary(input: INewEnquiryMessageInput) {
    return this.sendJson<INewEnquiryMessageInput, IEnquiryItineraryMessage>("POST", `/messages/enquiry-itinerary`, input);
  }

  public getEnquiriesPage(pageNumber?: number, pageSize?: number, params?: ApiParams.AdminEnquiryFilterSet) {
    const qsParams = {
      ...params,
      page: pageNumber < 1 ? 1 : pageNumber,
      pageSize,
    };

    return this.sendJson<void, IApiResponse<IEnquiry>>("GET", `/enquiries${formatQueryString(qsParams)}`);
  }

  public getTriagedEnquiries() {
    return this.sendJson<void, IApiResponse<IEnquiryRequiredActionOutput>>("GET", "/enquiries/triage");
  }

  public getLtosAdminStats(params?: ApiParams.AdminLtoStatsFilterSet) {
    return this.sendJson<void, IApiResponse<ILtoAdminStats[]>>("GET", `/ltos/admin-stats${formatQueryString({ ...params })}`);
  }

  public getAllEnquiryStats() {
    return this.sendJson<void, IApiResponse<IEnquiryStats>>("GET", `/enquiries/stats`);
  }

  public getLtoEnquiryStats(ltoId: number) {
    return this.sendJson<void, IApiResponse<IEnquiryStats>>("GET", `/ltos/${ltoId}/enquiries/stats`);
  }

  public getLtoFocusEnquiries(ltoId: number) {
    return this.sendJson<void, IApiResponse<IEnquiryRequiredActionOutput>>("GET", `/ltos/${ltoId}/enquiries/focus`);
  }

  public getLtoEnquiriesPage(
    ltoId: number,
    pageNumber?: number,
    pageSize?: number,
    params?: ApiParams.LtoEnquiryFilterSet
  ) {
    const qsParams = {
      ...params,
      page: pageNumber < 1 ? 1 : pageNumber,
      pageSize,
    };

    return this.sendJson<void, IApiResponse<IEnquiryOutput[]>>("GET", `/ltos/${ltoId}/enquiries${formatQueryString(qsParams)}`);
  }

  public getEnquiryByReference(ref: string) {
    return this.sendJson<void, IApiResponse<IEnquiryOutput>>("GET", `/enquiries-by-ref/${ref}`);
  }

  public toggleEnquiryAdminFollow(enquiryId: number, follow: boolean) {
    return follow
      ? this.sendJson<void, void>("PUT", `/enquiries/${enquiryId}/follow`)
      : this.sendJson<void, void>("DELETE", `/enquiries/${enquiryId}/follow`);
  }

  public getAllAgeRanges() {
    return this.sendJson<void, IApiResponse<IAgeRange[]>>("GET", `/age-ranges`);
  }

  public addNewTravelerDetails(input: INewTravelerDetailsInput) {
    return this.sendJson<INewTravelerDetailsInput, IFetchApiResponse<IEnquiryOutput>>("POST", `/traveler-details`, input);
  }

  public updateTravelerDetails(input: Partial<ITravelerDetailsUpdateInput>) {
    return this.sendJson<Partial<ITravelerDetailsUpdateInput>, IFetchApiResponse<IEnquiryOutput>>("PUT", `/traveler-details/${input.id}`, input);
  }

  public addNewLeadTravelerDetails(input: INewTravelerDetailsInput) {
    return this.sendJson<INewTravelerDetailsInput, IFetchApiResponse<IEnquiryOutput>>("POST", `/enquiries/${input.enquiryId}/lead-traveler-details`, input);
  }

  public updateLeadTravelerDetails(input: Partial<ILeadTravelerDetailsUpdateInput>) {
    return this.sendJson<Partial<ILeadTravelerDetailsUpdateInput>, IFetchApiResponse<IEnquiryOutput>>("PUT", `/enquiries/${input.enquiryId}/lead-traveler-details`, input);
  }

  public deleteTravelerDetails(travelerId: number) {
    return this.sendJson<void, void>("DELETE", `/traveler-details/${travelerId}`);
  }

  public getMessages(enquiryId: number) {
    return this.sendJson<void, IMessagesOutput>("GET", `/enquiries/${enquiryId}/messages`);
  }

  public sendTextMessageToCustomer(textMessage: INewEnquiryMessageInput) {
    return this.sendJson<INewEnquiryMessageInput, IMessage>("POST", `/messages/text/to-customer`, textMessage);
  }

  public sendTextMessageToLto(textMessage: INewEnquiryMessageInput) {
    return this.sendJson<INewEnquiryMessageInput, IMessage>("POST", `/messages/text/to-lto`, textMessage);
  }

  public getItineraries(enquiryId: number) {
    return this.sendJson<void, IEnquiryItineraryListItemsOutput>("GET", `/enquiries/${enquiryId}/itineraries`);
  }

  public toggleEnquiryConfirmed(enquiryId: number, confirmed: boolean) {
    return this.sendJson<void, IEnquiryItineraryListItemsOutput>(confirmed ? "PUT" : "DELETE", `/enquiries/${enquiryId}/confirmed`);
  }

  public toggleEnquiryDormant(enquiryId: number, dormant: boolean, dormantUntil: IDate) {
    return this.sendJson<void, IEnquiryItineraryListItemsOutput>(dormant ? "PUT" : "DELETE",
      `/enquiries/${enquiryId}/dormant?${dormantUntil ? `&dormantUntil=${dormantUntil.value}` : ""}`);
  }

  public getItinerariesPage(page = 1, pageSize = 50, apiParams?: ApiParams.ItineraryApiParams) {
    const params = {
      page,
      pageSize,
      ...apiParams,
    };
    return this.sendJson<void, IApiResponse<IItineraryListItem>>("GET", `/itineraries${formatQueryString(params)}`);
  }

  public getSampleItinerariesForLto(ltoId: number, currencyCode?: string) {
    return this.sendJson<void, IApiResponse<ISampleItineraryListItem[]>>("GET", `/ltos/${ltoId}/sample-itineraries`, null, currencyCode);
  }

  public getItinerary(itineraryId: number) {
    return this.sendJson<void, IEnquiryItinerarySummary>("GET", `/enquiry-itineraries/${itineraryId}`);
  }

  public getItineraryByQuote(quoteId: number) {
    return this.sendJson<void, IEnquiryItinerarySummary>("GET", `/enquiry-itinerary-quotes/${quoteId}`);
  }

  public getCurrenciesForQuote(quoteId: number) {
    return this.sendJson<void, IEnquiryItineraryCurrencies>("GET", `/enquiry-itinerary-quotes/${quoteId}/currencies`);
  }

  public getSampleItinerary(itinerarySlug: string, currencyCode?: string) {
    return this.sendJson<void, ISampleItinerarySummary>("GET", `/sample-itineraries/${itinerarySlug}`, null, currencyCode);
  }

  public createNewItinerary(newItinInput: INewEnquiryItineraryInput) {
    return this.sendJson<INewEnquiryItineraryInput, ICreationResult>("POST", `/enquiry-itineraries`, newItinInput);
  }

  public createNewSampleItinerary(newItinInput: INewSampleItineraryInput) {
    return this.sendJson<INewSampleItineraryInput, ICreationResult>("POST", `/sample-itineraries`, newItinInput);
  }

  public updateSampleItinerary(sampleItinInput: ISampleItineraryUpdateInput) {
    return this.sendJson<ISampleItineraryUpdateInput, void>("PUT", `/sample-itineraries/${sampleItinInput.slug}`, sampleItinInput);
  }

  public updateEnquiryItinerary(enquiryItinInput: IEnquiryItineraryUpdateInput) {
    return this.sendJson<IEnquiryItineraryUpdateInput, void>("PUT", `/enquiry-itineraries/${enquiryItinInput.id}`, enquiryItinInput);
  }

  public createNewItineraryDay(newItinDayInput: INewItineraryDayInput) {
    return this.sendJson<INewItineraryDayInput, ICreationResult>("POST", `/itinerary-days`, newItinDayInput);
  }

  public getItineraryDay(itineraryDayId: number) {
    return this.sendJson<void, IItineraryDay>("GET", `/itinerary-days/${itineraryDayId}`);
  }

  public deleteItineraryDay(itineraryDayId: number) {
    return this.sendJson<void, void>("DELETE", `/itinerary-days/${itineraryDayId}`);
  }

  public deleteSampleItinerary(itinerarySlug: string) {
    return this.sendJson<void, void>("DELETE", `/sample-itineraries/${itinerarySlug}`);
  }

  public updateSampleItineraryStatus(status: ISampleItineraryStatusUpdateInput) {
    return this.sendJson<ISampleItineraryStatusUpdateInput, IApiResponse<ISampleItinerarySummary>>("PUT", `/sample-itineraries/${status.slug}/status`, status);
  }

  public getEnquiriesForCustomer() {
    return this.sendJson<void, IEnquirySummariesOutput>("GET", `/users/me/enquiries`);
  }

  public getSampleItinerariesByLtoIdAndCountryId(ltoId: number, countrySlug?: string, currencyCode?: string) {
    return this.sendJson<void, IPage<ISampleItineraryListItem>>("GET", `/sample-itineraries?ltoId=${ltoId}${countrySlug ? `&countrySlug=${countrySlug}` : ""}`, null, currencyCode);
  }

  public createEnquiryItineraryLineItem(data: INewEnquiryItineraryLineItemInput) {
    return this.sendJson<INewEnquiryItineraryLineItemInput, ICreationResult>("POST", `/enquiry-itinerary-line-items`, data);
  }

  public updateEnquiryItineraryLineItem(data: IEnquiryItineraryLineItemUpdateInput) {
    return this.sendJson<IEnquiryItineraryLineItemUpdateInput, void>("PUT", `/enquiry-itinerary-line-items/${data.id}`, data);
  }

  public updateEnquiryItineraryPricingInformation(data: IEnquiryItineraryPricingInformationUpdateInput) {
    return this.sendJson<IEnquiryItineraryPricingInformationUpdateInput, IEnquiryItinerarySummary>("PUT", `/enquiry-itineraries/${data.id}/pricing-information`, data);
  }

  public deleteEnquiryItineraryLineItem(lineItemId: number) {
    return this.sendJson<void, void>("DELETE", `/enquiry-itinerary-line-items/${lineItemId}`);
  }

  public getItinerariesForEnquiry(enquiryId: number) {
    return this.sendJson<void, IEnquiryItineraryListItemsOutput>("GET", `/enquiries/${enquiryId}/itineraries`);
  }

  public deleteEnquiryItinerary(itineraryId: number) {
    return this.sendJson<void, void>("DELETE", `/enquiry-itineraries/${itineraryId}`);
  }

  public markItineraryViewed(quoteId: number) {
    return this.sendJson<void, void>("PUT", `/enquiry-itinerary-quotes/${quoteId}/viewed`);
  }

  public markMessagesRead(enquiryId: number) {
    return this.sendJson<void, void>("PUT", `/enquiries/${enquiryId}/up-to-date`);
  }

  public createNewEvent(event: Partial<IEvent>): Promise<IFetchApiResponse<IEvent>> {
    switch (event.type) {
      case ComponentType.Excursion:
        return this.createNewExcursion(EntityConverter.excursionToNewExcursionInput(event));
      case ComponentType.Transfer:
        return this.createNewTransfer(EntityConverter.transferToNewTransferInput(event));
      default:
        neverHere(event, "Unknown event type passed into createNewEvent");
    }
  }

  public createNewExcursion(newExcursionInput: INewExcursion) {
    return this.sendJson<INewExcursion, IExcursion>("POST", `/events/excursion`, newExcursionInput);
  }

  public createNewTransfer(newTransferInput: INewTransfer) {
    return this.sendJson<INewTransfer, ITransfer>("POST", `/events/transfer`, newTransferInput);
  }

  public updateEvent(event: Partial<IEvent>): Promise<IFetchApiResponse<IEvent>> {
    switch (event.type) {
      case ComponentType.Excursion:
        return this.updateExcursion(EntityConverter.excursionToExcursionUpdateInput(event));
      case ComponentType.Transfer:
        return this.updateTransfer(EntityConverter.transferToTransferUpdateInput(event));
      default:
        neverHere(event, "Unknown event type passed into updateEvent");
    }
  }

  public updateExcursion(excursionUpdateInput: IExcursionUpdate) {
    return this.sendJson<IExcursionUpdate, IExcursion>("PUT", `/events/${excursionUpdateInput.id}/excursion`, excursionUpdateInput);
  }

  public updateTransfer(transferUpdateInput: ITransferUpdate) {
    return this.sendJson<ITransferUpdate, ITransfer>("PUT", `/events/${transferUpdateInput.id}/transfer`, transferUpdateInput);
  }

  public getExcursionsByQuery(
    ltoId: number,
    page = 1,
    pageSize = 20,
    apiParams?: ApiParams.ComponentApiParams
  ): Promise<IFetchApiResponse<IApiResponse<IExcursion[]>>> {
    if (!ltoId) {
      throw new Error("LTO ID required to list excursions");
    }

    const params = {
      ltoId,
      page,
      pageSize,
      ...apiParams,
    };

    return this.sendJson<void, IApiResponse<IExcursion[]>>("GET", `/ltos/${ltoId}/excursions${formatQueryString(params)}`);
  }

  public getAllExcursionsByQuery(
    page = 1,
    pageSize = 20,
    apiParams?: ApiParams.ComponentApiParams
  ): Promise<IFetchApiResponse<IApiResponse<IExcursion[]>>> {
    const params = {
      page,
      pageSize,
      ...apiParams,
    };

    return this.sendJson<void, IApiResponse<IExcursion[]>>("GET", `/events/excursions${formatQueryString(params)}`);
  }

  public getTransfersByQuery(
    ltoId: number,
    page = 1,
    pageSize = 20,
    apiParams?: ApiParams.ComponentApiParams
  ): Promise<IFetchApiResponse<IApiResponse<ITransfer[]>>> {
    if (!ltoId) {
      throw new Error("LTO ID required to list transfers");
    }

    const params = {
      ltoId,
      page,
      pageSize,
      ...apiParams,
    };

    return this.sendJson<void, IApiResponse<ITransfer[]>>("GET", `/ltos/${ltoId}/transfers${formatQueryString(params)}`);
  }

  public getAllTransfersByQuery(
    page = 1,
    pageSize = 20,
    apiParams?: ApiParams.ComponentApiParams
  ): Promise<IFetchApiResponse<IApiResponse<ITransfer[]>>> {
    const params = {
      page,
      pageSize,
      ...apiParams,
    };

    return this.sendJson<void, IApiResponse<ITransfer[]>>("GET", `/events/transfers${formatQueryString(params)}`);
  }

  public deleteEvent(eventId: number) {
    return this.sendJson<void, void>("DELETE", `/events/${eventId}`);
  }

  public addEventToItineraryDay(addEventToItineraryDayInput: IAddEventToItineraryDayInput) {
    return this.sendJson<IAddEventToItineraryDayInput, IItineraryDayEvent>("POST", `/itinerary-day-events`, addEventToItineraryDayInput);
  }

  public updateEventForItineraryDay(dayUpdateInput: IItineraryDayEventUpdateInput) {
    return this.sendJson<IItineraryDayEventUpdateInput, void>("PUT", `/itinerary-day-events/${dayUpdateInput.id}`, dayUpdateInput);
  }

  public deleteEventForItineraryDay(itineraryDayEventId: number) {
    return this.sendJson<void, void>("DELETE", `/itinerary-day-events/${itineraryDayEventId}`);
  }

  public setEventOrderInItineraryDay(itineraryDayId: number, idList: number[]) {
    return this.sendJson<IItineraryDayEventsOrderInput, IItineraryDay>("PUT", `/itinerary-days/${itineraryDayId}/order-events`, { order: idList });
  }

  public getAccommodationById(accommodationId: number) {
    return this.sendJson<void, IAccommodation>("GET", `/accommodations/${accommodationId}`);
  }

  public createNewAccommodation(ltoId: number, accommodation: Partial<IAccommodation>) {
    const input = EntityConverter.accommodationToNewAccommodationInput(ltoId, accommodation);
    return this.sendJson<INewAccommodation, IAccommodation>("POST", `/accommodations`, input);
  }

  public updateAccommodation(accommodation: Partial<IAccommodation>) {
    const input = EntityConverter.accommodationToAccommodationUpdateInput(accommodation);
    return this.sendJson<IAccommodationUpdate, IAccommodation>("PUT", `/accommodations/${input.id}`, input);
  }

  public getAccommodationsByQuery(
    ltoId: number,
    page = 1,
    pageSize = 20,
    apiParams?: ApiParams.ComponentApiParams
  ): Promise<IFetchApiResponse<IApiResponse<IAccommodation[]>>> {
    if (!ltoId) {
      throw new Error("LTO ID required to list accommodations");
    }

    const params = {
      ltoId,
      page,
      pageSize,
      ...apiParams,
    };

    return this.sendJson<void, IApiResponse<IAccommodation[]>>("GET", `/ltos/${ltoId}/accommodations${formatQueryString(params)}`);
  }

  public getAllAccommodationsByQuery(
    page = 1,
    pageSize = 20,
    apiParams?: ApiParams.ComponentApiParams
  ): Promise<IFetchApiResponse<IApiResponse<IAccommodation[]>>> {
    const params = {
      page,
      pageSize,
      ...apiParams,
    };

    return this.sendJson<void, IApiResponse<IAccommodation[]>>("GET", `/accommodations${formatQueryString(params)}`);
  }

  public deleteAccommodation(accommodationId: number) {
    return this.sendJson<void, void>("DELETE", `/accommodations/${accommodationId}`);
  }

  public setAccommodationForItineraryDay(input: ISetAccommodationForItineraryDayInput) {
    return this.sendJson<ISetAccommodationForItineraryDayInput, IItineraryDay>("POST", `/itinerary-days/${input.itineraryDayId}/accommodation`, input);
  }

  public updateAccommodationForItineraryDay(
    itineraryDayId: number,
    itineraryDayAccommodationUpdate: IItineraryDayAccommodationUpdate
  ) {
    return this.sendJson<IItineraryDayAccommodationUpdate, void>("PUT", `/itinerary-days/${itineraryDayId}/accommodation`, itineraryDayAccommodationUpdate);
  }

  public deleteAccommodationForItineraryDay(itineraryDayId: number) {
    return this.sendJson<void, void>("DELETE", `/itinerary-days/${itineraryDayId}/accommodation`);
  }

  public setAccommodationTypeForItineraryDay(
    itineraryDayId: number,
    itineraryDayAccommodationTypeUpdateInput: IItineraryDayAccommodationTypeUpdateInput
  ) {
    return this.sendJson<IItineraryDayAccommodationTypeUpdateInput, void>("PUT", `/itinerary-days/${itineraryDayId}/accommodation-type`, itineraryDayAccommodationTypeUpdateInput);
  }

  public getLocations(params?: Partial<ApiParams.LocationFilterSet>) {
    return this.sendJson<void, IPage<ILocationSummary>>("GET", `/locations${formatQueryString(params)}`);
  }

  public getCountriesByRegionSlug(regionSlug: string, isPrimary?: boolean) {
    return this.sendJson<void, ICountriesOutput>("GET", `/regions/${regionSlug}/countries`, null, null, isPrimary);
  }

  public getDestinationsByCountrySlug(countrySlug: string) {
    return this.sendJson<void, IDestinationsOutput>("GET", `/countries/${countrySlug}/destinations`);
  }

  public getTeamProfiles() {
    return this.sendJson<void, IAboutTeamProfiles<IUserPublic>>("GET", `/users/team-order`);
  }

  public getBookingStatistics(range: string) {
    const qsParams = {
      range,
    };
    return this.sendJson<void, IBookingStatistics>("GET", `/statistics/bookings${formatQueryString(qsParams)}`);
  }

  public getLocationBySlug(locationSlug: string, destinationType?: LocationType, isPrimary?: boolean) {
    const qsParams = {
      destinationType,
    };
    return this.sendJson<void, ILocation>("GET", `/locations/${locationSlug}${formatQueryString(qsParams)}`, null, null, isPrimary);
  }

  public setLocationStatus(locationStatus: ILocationStatusUpdateInput) {
    return this.sendJson<ILocationStatusUpdateInput, ILocation>("PUT", `/locations/${locationStatus.id}/status`, locationStatus);
  }

  public deleteLocation(locationId: number) {
    return this.sendJson<void, void>("DELETE", `/locations/${locationId}`);
  }

  public linkLocationImage(input: ILinkLocationImageInput) {
    return this.sendJson<ILinkLocationImageInput, ILocation>("POST", `/locations/${input.locationId}/image`, input);
  }

  public unlinkLocationImage(input: ILinkLocationImageInput) {
    return this.sendJson<ILinkLocationImageInput, ILocation>("DELETE", `/locations/${input.locationId}/image`, input);
  }

  public deleteImage(imageId: number) {
    return this.sendJson<void, ICreationResult>("DELETE", `/images/${imageId}`);
  }

  public deleteLocationImage(imageId: number) {
    return this.sendJson<void, ICreationResult>("DELETE", `/location-images/${imageId}`);
  }

  public updateLocationImageDescription(input: ILocationImageUpdateInput) {
    return this.sendJson<ILocationImageUpdateInput, void>("PUT", `/location-images/${input.id}`, input);
  }

  public getLtoConfigForLocation(locationId: number) {
    return this.sendJson<void, IApiResponse<ILtoLocation[]>>("GET", `/locations/${locationId}/ltos-config`);
  }

  public getLocationConfigForLto(ltoId: number) {
    return this.sendJson<void, IApiResponse<ILtoLocation[]>>("GET", `/ltos/${ltoId}/location-config`);
  }

  /**
   * Unified endpoint for creating locations of any type.
   */
  public createLocation(updateInput: INewLocationInput) {
    return this.sendJson("POST", `/locations`, updateInput);
  }

  /**
   * Unified endpoint for updating locations of any type.
   */
  public updateLocation(updateInput: ILocationUpdateInput) {
    return this.sendJson("PUT", `/locations/${updateInput.id}`, updateInput);
  }

  public updateLocationWeather(locationWeatherUpdateInput: ILocationWeatherUpdateInput) {
    return this.sendJson<ILocationWeatherUpdateInput, void>("PUT", `/locations/${locationWeatherUpdateInput.id}/weather`, locationWeatherUpdateInput);
  }

  public getExcursionsByCountryId(countryId: number) {
    return this.sendJson<void, IPage<IExcursion>>("GET", `/countries/${countryId}/excursions`);
  }

  public getProfile() {
    return this.sendJson<void, IApiResponse<IUser>>("GET", `/users/me`);
  }

  public getThemePage(page: number, pageSize: number, params?: ApiParams.ThemesFilterSet) {

    const qsParams = {
      page: page < 1 ? 1 : page,
      pageSize,
      inspiration: params.inspiration,
      enquiry: params.enquiry,
      query: params.nameContains,
    };

    return this.sendJson<void, IApiResponse<ITheme[]>>("GET", `/themes${formatQueryString(qsParams)}`);
  }

  public createBookingDigitalPayment(createBookingPaymentInput: ICreateBookingPaymentInput) {
    return this.sendJson<ICreateBookingPaymentInput, IPaymentParameters>("POST", `/digital-payments/booking`, createBookingPaymentInput);
  }

  public createInstallmentDigitalPayment(createInstallmentPaymentInput: ICreateInstallmentPaymentInput) {
    return this.sendJson<ICreateInstallmentPaymentInput, IPaymentParameters>("POST", `/digital-payments/installment`, createInstallmentPaymentInput);
  }

  public completeDigitalPayment(finalizePaymentInput: IFinalizePaymentInput) {
    return this.sendJson<IFinalizePaymentInput, void>("PUT", `/digital-payments/${finalizePaymentInput.id}/complete`, finalizePaymentInput);
  }

  public cancelDigitalPayment(finalizePaymentInput: IFinalizePaymentInput) {
    return this.sendJson<IFinalizePaymentInput, void>("PUT", `/digital-payments/${finalizePaymentInput.id}/cancel`, finalizePaymentInput);
  }

  public getPayments(filters: ApiParams.PaymentsFilterSet, page: number, pageSize: number) {
    const qsParams = {
      page: page < 1 ? 1 : page,
      pageSize,
      currencies: filters.currencies,
      statuses: filters.statuses,
      start: filters.start ? DateUtil.toMoment(filters.start).toISOString() : null,
      end: filters.end ? DateUtil.toMoment(filters.end).toISOString() : null,
    };

    return this.sendJson<void, IApiResponse<IPayment[]>>("GET", `/payments${formatQueryString(qsParams)}`);
  }

  public getPaymentsForEnquiry(enquiryId: number, statuses?: PaymentStatus[]) {
    const qsParams = {
      statuses,
    };

    return this.sendJson<void, IPaymentsOutput>("GET", `/enquiries/${enquiryId}/payments${formatQueryString(qsParams)}`);
  }

  public updateDigitalPayment(digitalPaymentUpdate: IDigitalPaymentUpdate) {
    return this.sendJson<IDigitalPaymentUpdate, IDigitalPayment>("PUT", `/digital-payments/${digitalPaymentUpdate.id}`, digitalPaymentUpdate);
  }

  public createNonDigitalPayment(nonDigitalPayment: INewNonDigitalPaymentInput) {
    return this.sendJson<INewNonDigitalPaymentInput, void>("POST", `/non-digital-payments`, nonDigitalPayment);
  }

  public createRefund(refund: INewRefundInput) {
    return this.sendJson<INewNonDigitalPaymentInput, void>("POST", `/refunds`, refund);
  }

  public getContentById(id: string, currencyCode?: string) {
    return this.sendJson<void, IApiResponse<IContent>>("GET", `/content/${id}`, null, currencyCode);
  }

  public getLocationContent(slug: string) {
    return this.sendJson<void, ILocationContent>("GET", `/locations/${slug}/sections`, null);
  }

  public getContentPage(filters: Partial<IListContentParams>, page: number, pageSize: number, currencyCode?: string) {
    const qsParams = {
      page: page < 1 ? 1 : page,
      pageSize,
      themes: filters.themes,
      months: filters.months,
      locations: filters.locations,
      ltos: filters.ltos,
      types: filters.types,
      statuses: filters.statuses,
      query: filters.query,
      start: filters.start ? DateUtil.toMoment(filters.start).toISOString() : null,
      end: filters.end ? DateUtil.toMoment(filters.end).toISOString() : null,
      overallRating: filters.overallRating,
      isGroupTour: filters.isGroupTour,
    };

    return this.sendJson<void, IApiResponse<IContent[]>>("GET", `/content${formatQueryString(qsParams)}`, null, currencyCode);
  }

  public createContent(content: IContentInput) {
    if (content == null) {
      throw new Error("Can't create null content object");
    }

    return this.sendJson<IContentInput, IApiResponse<IContent>>(
      "POST",
      "/content",
      EntityConverter.contentToCreateInput(content)
    );
  }

  public updateContent(input: Partial<IContentInput>) {
    if (input == null || input.id == null) {
      throw new Error("Can't update content, either null or no id");
    }

    return this.sendJson<IContentInput, IApiResponse<IContent>>("PUT", `/content/${input.id}`, input as IContentInput);
  }

  public getThemeById(id: number) {
    return this.sendJson<void, IApiResponse<ITheme>>("GET", `/themes/${id}`);
  }

  public createTheme(theme: ITheme) {
    if (theme == null) {
      throw new Error("Can't update theme, either null or no id");
    }

    if (theme.id != null) {
      throw new Error("Can't create a theme with an id");
    }

    return this.sendJson<IThemeInput, IApiResponse<ITheme>>("POST", `/themes`, EntityConverter.themeToUpdateInput(theme));
  }

  public updateTheme(theme: ITheme) {
    if (theme == null || theme.id == null) {
      throw new Error("Can't update theme, either null or no id");
    }

    return this.sendJson<IThemeInput, IApiResponse<ITheme>>("PUT", `/themes/${theme.id}`, EntityConverter.themeToUpdateInput(theme));
  }

  public deleteTheme(id: number) {
    if (id == null) {
      throw new Error("Can't delete a theme with null id");
    }

    return this.sendJson<void, IApiResponse<unknown>>("DELETE", `/themes/${id}`);
  }

  /**
   * Deprecated: will be removed when old blog post detail page is killed. Use getContentById instead
   * @param slug
   */
  public getBlogPostBySlug(slug: string) {
    return this.sendJson<void, IContent>("GET", `/content/slug/${slug}`);
  }

  public getAllCurrencies() {
    return this.sendJson<void, ICurrency[]>("GET", `/currencies`);
  }

  public getCustomerCurrencies() {
    return this.sendJson<void, IBaseCurrency[]>("GET", `/currencies-list`);
  }

  public pollUploadStatus(jobName: string) {
    return this.sendJson<void, IUploadJob>("GET", `/upload-jobs/${jobName}`);
  }

  public generatePdfJob(itineraryId: number) {
    return this.sendJson<void, number>("GET", `/enquiry-itineraries/${itineraryId}/pdf`);
  }

  public generateAtolCertificateJob(enquiryId: number) {
    return this.sendJson<void, number>("GET", `/enquiries/${enquiryId}/atol-certificate`);
  }

  public pollPdfJob(jobId: string) {
    return this.sendJson<void, IPdfJob>("GET", `/pdf-jobs/${jobId}`);
  }

  public checkEmailAddress(emailAddress: string) {
    return this.sendJson<{ emailAddress: string }, IUserTypeOutput>("POST", `/email-addresses`, { emailAddress });
  }

  public updateExchangeRate(input: ICustomerLtoExchangeRatesUpdateInput) {
    return this.sendJson<ICustomerLtoExchangeRatesUpdateInput, void>("PUT", `/currencies/${input.currencyId}/exchange-rates`, input);
  }

  public logError(details: { message: string; stack?: string }) {
    return this.sendJson<typeof details, IClientLogResult>("POST", `/client-log/error`, details);
  }

  public getIsoCountries(query: string, page: number) {
    return this.sendJson<void, IPage<IIsoCountry>>("GET", `/iso-countries?query=${query || ""}&page=${page || 1}`);
  }

  public getCardTypes() {
    return this.sendJson<void, ICardType[]>("GET", `/card-types`);
  }

  public getImageByFileName(fileName: string) {
    return this.sendJson<void, ILocationImage>("GET", `/images/location/${fileName}`);
  }

  public requestCancellation(cancellationRequest: IEnquiryCancellationRequestUpdate) {
    return this.sendJson<IEnquiryCancellationRequestUpdate, void>("PUT", `/enquiries/${cancellationRequest.id}/cancellation-request`, cancellationRequest);
  }

  public cancel(enquiryId: number) {
    return this.sendJson<void, void>("PUT", `/enquiries/${enquiryId}/cancellation`);
  }

  public unrequestCancellation(enquiryId: number) {
    return this.sendJson<void, void>("DELETE", `/enquiries/${enquiryId}/cancellation-request`);
  }

  public getReview(reviewId: number) {
    return this.sendJson<void, IReview>("GET", `/reviews/${reviewId}`);
  }

  public updateReview(review: IReviewUpdateInput) {
    if (review == null || review.id == null) {
      throw new Error("Can't update content, either null or no id");
    }

    return this.sendJson<IReviewInput, IApiResponse<IReview>>("PUT", `/reviews/${review.id}`, review);
  }

  public getAllReviews(pageNumber?: number) {
    return this.sendJson<void, IPage<IReview>>("GET", `/reviews${pageNumber ? `?page=${pageNumber}` : ""}`);
  }

  public getLtoReviews(ltoId: number, pageNumber?: number) {
    return this.sendJson<void, IPage<IReview>>("GET", `/ltos/${ltoId}/reviews${pageNumber ? `?page=${pageNumber}` : ""}`);
  }

  public createReview(input: IReviewInput) {
    return this.sendJson<IReviewInput, IReview>("POST", `/reviews`, input);
  }

  public publishReview(reviewId: number) {
    return this.sendJson<void, IReview>("PUT", `/reviews/${reviewId}/published`);
  }

  public deleteReview(reviewId: number) {
    return this.sendJson<void, void>("DELETE", `/reviews/${reviewId}`);
  }

  public replyToReview(reviewReply: IReviewReplyInput) {
    return this.sendJson<IReviewReplyInput, void>("PUT", `/reviews/${reviewReply.id}/reply`, reviewReply);
  }

  public getSnippetsPage(page = 1, pageSize = 50, apiParams?: ApiParams.SnippetsApiParams) {
    const params = {
      page,
      pageSize,
      ...apiParams,
    };
    return this.sendJson<void, IApiResponse<ISnippet>>("GET", `/snippets${formatQueryString(params)}`);
  }

  public updateSnippet(updatedSnippet: ISnippetInput) {
    return this.sendJson("PUT", `/snippets/${updatedSnippet.id}`, updatedSnippet);
  }

  public createSnippet(createdSnippet: ISnippetInput) {
    return this.sendJson<ISnippetInput, ISnippet>("POST", `/snippets`, createdSnippet);
  }

  public getSnippetById(snippetId: number) {
    return this.sendJson<void, ISnippet>("GET", `/snippets/${snippetId}`);
  }

  public syncContactWithHubspot(userId: number) {
    return this.sendJson<number, void>("PUT", `/users/${userId}/sync-to-hubspot`);
  }

  public getUserGlobalPermissions(userId: number) {
    return this.sendJson<void, IGlobalUserPermission[]>("GET", `/users/${userId}/permissions`);
  }

  public login(model: ILoginRequest) {
    return this.sendJson<ILoginRequest, IAuthorizeGrantResponse>("POST", "/auth/login", model);
  }

  public forgotPassword(model: IForgottenPasswordRequest) {
    return this.sendJson<IForgottenPasswordRequest, void>("POST", "/auth/forgot-password", model);
  }

  public resetPassword(model: IResetPasswordRequest) {
    return this.sendJson<IResetPasswordRequest, void>("POST", "/auth/reset-password", model);
  }

  public verify(model: IVerifyRequest) {
    return this.sendJson<IVerifyRequest, void>("POST", "/auth/verify", model);
  }

  public getUsersPage(page = 1, pageSize = 50, apiParams?: ApiParams.UsersApiParams) {
    const params = {
      page,
      pageSize,
      ...apiParams,
    };

    return this.sendJson<void, IApiResponse<IUserSummary[]>>("GET", `/users${formatQueryString(params)}`);
  }

  public inputSetting(input: ISettingInput) {
    return this.sendJson<ISettingInput, IApiResponse<ISetting>>("PUT", `/settings/${input.key}`, input);
  }

  public getEnquiryTerms(enquiryId: number) {
    return this.sendJson<void, IBookingTerms>("GET", `/enquiries/${enquiryId}/terms`);
  }
}
